import { useState, useEffect, useRef } from 'react';
import { Users, ChevronDown, X } from 'lucide-react';
import { typedApi } from '../api/typed';
import { useResource } from '../hooks/useResource';

export default function StaffPicker({ value, staffId, onChange, businessId, compact }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const { data: staff } = useResource(
    'staff-picker',
    async ({ signal }) => await typedApi.get('/api/staff', { signal }),
    { initialData: [] },
  );

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = staff.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (businessId && s.businessIds?.length > 0 && !s.businessIds.includes(businessId)) return false;
    return true;
  });

  const selected = staffId ? staff.find((s) => s.id === staffId) : null;

  const handleSelect = (s) => {
    onChange({ staffId: s.id, delegatedTo: s.name });
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ staffId: null, delegatedTo: '' });
  };

  if (compact) {
    return (
      <div ref={ref} className="relative">
        <button
          data-testid="staff-picker-compact"
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors"
          style={{ background: selected ? 'var(--pd-focus)' : 'var(--pd-panel-2)', color: selected ? 'white' : 'var(--pd-muted)' }}
        >
          <Users className="w-3 h-3" />
          {selected ? selected.name.split(' ')[0] : 'Delegate'}
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 z-50 w-56 rounded-2xl overflow-hidden animate-fade-in"
            style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', boxShadow: 'var(--pd-shadow-3)' }}>
            <div className="p-2">
              <input
                data-testid="staff-picker-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff..."
                className="w-full px-3 py-2 rounded-xl text-[12px] outline-none"
                style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-text)' }}
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
                  {staff.length === 0 ? 'No staff added yet' : 'No matches'}
                </div>
              ) : filtered.map((s) => (
                <button
                  key={s.id}
                  data-testid={`staff-option-${s.id}`}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-black/[0.03]"
                  style={{ background: s.id === staffId ? 'var(--pd-panel-2)' : 'transparent' }}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                    style={{ background: 'var(--pd-focus)' }}>
                    {s.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium truncate" style={{ color: 'var(--pd-text)' }}>{s.name}</div>
                    {s.role && <div className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>{s.role}</div>}
                  </div>
                  {s.activeDelegations > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}>
                      {s.activeDelegations}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>
        <Users className="w-3 h-3 inline mr-1" />Delegate to
      </label>
      <button
        data-testid="staff-picker-button"
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-[16px] text-sm text-left"
        style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: selected ? 'var(--pd-text)' : 'var(--pd-subtle)' }}
      >
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0" style={{ background: 'var(--pd-focus)' }}>
                {selected.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
              </span>
              {selected.name}
              {selected.role && <span className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>({selected.role})</span>}
            </>
          ) : 'Select staff member...'}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <X
              className="w-3.5 h-3.5 cursor-pointer transition-colors"
              style={{ color: 'var(--pd-subtle)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--pd-danger)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--pd-subtle)'; }}
              onClick={handleClear}
            />
          )}
          <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-2xl overflow-hidden animate-fade-in"
          style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)', boxShadow: 'var(--pd-shadow-3)' }}>
          <div className="p-2">
            <input
              data-testid="staff-picker-modal-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff..."
              className="w-full px-3 py-2 rounded-xl text-[12px] outline-none"
              style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-text)' }}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto custom-scrollbar">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
                {staff.length === 0 ? 'Add staff in Delegatables tab first' : 'No matches'}
              </div>
            ) : filtered.map((s) => (
              <button
                key={s.id}
                data-testid={`staff-modal-option-${s.id}`}
                type="button"
                onClick={() => handleSelect(s)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-black/[0.03]"
                style={{ background: s.id === staffId ? 'var(--pd-panel-2)' : 'transparent' }}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ background: 'var(--pd-focus)' }}>
                  {s.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium truncate" style={{ color: 'var(--pd-text)' }}>{s.name}</div>
                  {s.role && <div className="text-[10px] truncate" style={{ color: 'var(--pd-subtle)' }}>{s.role}</div>}
                </div>
                {s.activeDelegations > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}>
                    {s.activeDelegations} active
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
